import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { JiraLinkedService } from 'app/model/linkedservice/jira.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { CONNECTOR_VERSION20 } from 'app/services/globals';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(JiraLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                host: '*',
                port: '*',
                username: '*',
                password: 'secret',
                useEncryptedEndpoints: '*',
                useHostVerification: '*',
                usePeerVerification: '*',
                encryptedCredential: '*'
            }
        }
    }
})
export class JiraLinkedServiceAdapter extends LinkedServiceAdapter<JiraLinkedService> {
    protected override toAzureTypeProperties(): Partial<Model.JiraLinkedServiceTypeProperties> {
        return {};
    }
    protected modelConstructor(): new () => JiraLinkedService {
        return JiraLinkedService;
    }

    protected model(): IModel {
        return JiraLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: JiraLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.JiraLinkedService;
        properties.type = Metamodel.modelFor(JiraLinkedService).name;

        if (linkedService.version === driverVersions.v2) {
            properties.version = CONNECTOR_VERSION20;
            properties.typeProperties = this._buildTypeProperties(linkedService);
            if (!arrayIsNullOrEmpty(linkedService.additionalFields)) {
                linkedService.additionalFields.forEach((field) => {
                    properties.typeProperties.connectionProperties[field.key] = field.value;
                });
            }
        } else { 
            properties.typeProperties = {
                ...this._buildTypeProperties(linkedService),
                useEncryptedEndpoints: linkedService.useEncryptedEndpoints,
                useHostVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.useHostVerification : undefined,
                usePeerVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.usePeerVerification : undefined
            }
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: JiraLinkedService): Promise<JiraLinkedService> {
        const properties = linkedServiceResource.properties as Model.JiraLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.JiraLinkedServiceTypeProperties = properties.typeProperties;

        linkedService.version =
            __IS_FABRIC_DPE__ || properties.version === CONNECTOR_VERSION20
            ? driverVersions.v2
            : driverVersions.v1;
        linkedService.isLegacyConnector = linkedService.version !== driverVersions.v2;
        linkedService.host = typeProperties.host;
        linkedService.port = typeProperties.port;
        linkedService.username = typeProperties.username;
        linkedService.password = secretBaseToPasswordOrAKV(typeProperties.password, true, this.context);

        if (linkedService.version == driverVersions.v1) {
            linkedService.useEncryptedEndpoints = typeProperties.useEncryptedEndpoints;
            linkedService.useHostVerification = typeProperties.useHostVerification;
            linkedService.usePeerVerification = typeProperties.usePeerVerification;
        }
        return linkedService;
    }

    private _buildTypeProperties(linkedService: JiraLinkedService) {
        return {
            host: linkedService.host,
            port: linkedService.port,
            username: linkedService.username,
            password: passwordOrAKVtoSecretBase(linkedService.password, this.context)
        };
    }
}
